<p-confirmDialog
	key="deleteQuote"
	appendTo="body"/>

<div class="page">
	<div class="page-header">
		<h1 class="page-title">Quote Log</h1>
	</div>

	<div class="table-card p-card">
		<app-quote-filters
			[quotes]="allQuotes()"
			[isSyncing]="isSyncing()"
			(filtersApplied)="onFiltersApplied($event)"
			(refreshQuotes)="syncStatuses()"
			(downloadRequested)="openExportDialog()"/>

		@if (viewport.isMobile()) {
			<!-- Mobile Card View -->
			<div class="quote-cards">
				@if (isLoading()) {
					<div class="card-empty-state">
						<app-embark-spinner
							[size]="'3'"
							[strokeWidth]="'6'"></app-embark-spinner>
						<p>Loading quotes...</p>
					</div>
				} @else if (quotes().length === 0) {
					<div class="card-empty-state">
						<i class="pi pi-inbox"></i>
						<p>No quotes found</p>
					</div>
				} @else {
					@for (quote of paginatedQuotes(); track quote.id) {
						<div
							class="quote-card"
							(click)="openDetailPopover(quote)">
							<!-- Card Header -->
							<div class="card-top">
								<div class="card-top-header">
									<div class="card-customer">{{ quote.customer }}</div>
									<button
										type="button"
										class="card-delete-btn"
										(click)="confirmDeleteQuote(quote, $event)"
										aria-label="Delete quote">
										<i class="pi pi-trash"></i>
									</button>
								</div>
								<div class="card-received">{{ quote.receivedLabel }}</div>
							</div>

							<!-- Card Summary -->
							<div
								class="card-summary"
								(click)="openEmailModal(quote); $event.stopPropagation()">
								<span class="card-summary-text">{{ quote.emailSummary }}</span>
								@if (quote.hasThreadReplies && quote.threadReplies) {
									<span class="reply-badge">
                                        {{ quote.threadReplies.length }} repl{{
											quote.threadReplies.length === 1 ? "y" : "ies"
										}}
                                    </span>
								}
								@if (quote.attachments.length) {
									<span class="attachment-badge">
                                        <i class="pi pi-paperclip"></i>
										{{ quote.attachments.length }}
                                    </span>
								}
							</div>

							<!-- Card Rows -->
							<div
								class="card-row"
							>
								<span class="card-label">Due</span>
								<span>
								<p-date-picker
									#cardDueDatePicker
									[ngModel]="quote.dueDateIsoUtc"
									[showTime]="true"
									[hourFormat]="'12'"
									[showSeconds]="false"
									[inline]="false"
									[showOnFocus]="true"
									appendTo="body"
									class="due-date-picker cursor-pointer"
									[pTooltip]="quote.dueDate
												? 'Click to edit'
												: 'Click to set due date'
										"
									[tooltipPosition]="'top'"
									[invalid]="quote.dueDate == null"
									[placeholder]="quote.dueDate == null ? 'Set Due Date' : ''"
									[showButtonBar]="true"
									[hideOnDateTimeSelect]="false"
									panelStyleClass="due-date-panel"
								>
									<ng-template #buttonbar>
										<p-button
											size="small"
											label="Save Due Date"
											(onClick)="saveDueDate(cardDueDatePicker, quote)"
										/>
									</ng-template>
								</p-date-picker>
								</span>
							</div>

							<div class="card-row">
								<span class="card-label">Outside Rep</span>
								<span class="card-value">{{ quote.outsideRep.name }}</span>
							</div>

							<div class="card-row">
								<span class="card-label">Customer Match</span>
								@if (customerIsDefaultOrEmpty(quote)) {
									<span class="card-value card-value-warning">
                                        <i class="pi pi-exclamation-triangle"></i>
                                        Credit Card Sale
                                    </span>
								} @else {
									<span class="card-value">{{ quote.nsCustomerName }}</span>
								}
							</div>

							<div
								class="card-row"
								(click)="$event.stopPropagation()">
								<span class="card-label">Inside Rep</span>
								@if (quote.pendingIsrRep; as pendingRep) {
									<div class="isr-pending">
										<span class="isr-pending-text">Assigning to {{ pendingRep.fullName }}…</span>
										<button
											type="button"
											class="isr-undo-btn"
											(click)="undoInsideRepAssignment(quote.id)">
											Undo
										</button>
									</div>
								} @else {
									<p-select
										[options]="insideSalesReps()"
										[ngModel]="getSelectedIsr(quote)"
										(ngModelChange)="setInsideRep(quote.id, $event)"
										[disabled]="
                                            quote.isEstimateCreating ||
                                            !quote.dueDate ||
                                            !quote.nsCustomerId ||
                                            !userIsSalesManager
                                        "
										optionLabel="fullName"
										placeholder="— Assign —"
										[filter]="true"
										filterBy="fullName"
										[showClear]="!quote.nsEstimateId"
										styleClass="card-select"
										appendTo="body"
										[pTooltip]="getIsrTooltip(quote)"
										tooltipPosition="top"></p-select>
								}
							</div>

							<div class="card-row">
								<span class="card-label">Estimate #</span>
								@if (quote.estimateNumber && quote.nsEstimateId) {
									<a
										class="card-value card-link"
										[href]="
                                            'https://1103055.app.netsuite.com/app/accounting/transactions/estimate.nl?id=' +
                                            quote.nsEstimateId
                                        "
										target="_blank"
										(click)="$event.stopPropagation()">
										{{ quote.estimateNumber }}
										<i class="pi pi-external-link"></i>
									</a>
								} @else {
									<span class="card-value dash">—</span>
								}
							</div>

							<div class="card-status-row">
								<div class="status-item">
									<span class="card-label">NS Status</span>
									<span class="card-value">{{ quote.nsEstimateStatus ?? "—" }}</span>
								</div>
							</div>
						</div>
					}

					<!-- Mobile Card Pagination -->
					@if (quotes().length > 0) {
						<div class="card-pagination">
							<button
								type="button"
								class="pagination-btn"
								(click)="previousCardPage()"
								[disabled]="currentCardPage() === 0"
								aria-label="Previous page">
								<i class="pi pi-chevron-left"></i>
							</button>

							<div class="pagination-info">
                                <span class="pagination-text">
                                    Page {{ currentCardPage() + 1 }} of {{ totalCardPages() }}
                                </span>
							</div>

							<button
								type="button"
								class="pagination-btn"
								(click)="nextCardPage()"
								[disabled]="currentCardPage() === totalCardPages() - 1"
								aria-label="Next page">
								<i class="pi pi-chevron-right"></i>
							</button>
						</div>
					}
				}
			</div>
		} @else {
			<!-- Desktop Table View -->
			<div class="table-scroll">
				<p-table
					[value]="quotes()"
					class="p-datatable-sm quote-table"
					[scrollable]="true"
					scrollHeight="flex"
					sortMode="single"
					[paginator]="true"
					[rows]="pageSize()"
					[rowsPerPageOptions]="[50, 75, 100]"
					[paginatorDropdownAppendTo]="'body'">
					<ng-template pTemplate="header">
						<tr>
							<th pSortableColumn="receivedDate">
								Received
								<p-sortIcon field="receivedDate"/>
							</th>
							<th pSortableColumn="dueDate">
								Due
								<p-sortIcon field="dueDate"/>
							</th>
							<th pSortableColumn="hoursRemaining">
								Hours
								<p-sortIcon field="hoursRemaining"/>
							</th>
							<th>Customer</th>
							<th>Email Summary</th>
							<th>Outside Rep</th>
							<th pSortableColumn="insideRep">
								Inside Rep
								<p-sortIcon field="insideRep"/>
							</th>
							<th>Estimate #</th>
							<th pSortableColumn="nsEstimateStatus">
								NS Status
								<p-sortIcon field="nsEstimateStatus"/>
							</th>
							<th pSortableColumn="nsQuoteStatus">
								Quote Status
								<p-sortIcon field="nsQuoteStatus"/>
							</th>
							<th class="col-errors">Errors</th>
							<th class="col-quote-actions"></th>
						</tr>
					</ng-template>

					<ng-template
						pTemplate="body"
						let-quote>
						<tr
							[class.row-due-defaulted]="quote.dueDateDefaulted"
							[class.row-clickable]="quote.nsEstimateId">
							<td class="col-received">{{ quote.receivedLabel }}</td>
							<td class="col-due">
								<span>
									<p-date-picker
										#dueDatePicker
										[ngModel]="quote.dueDateIsoUtc"
										[showTime]="true"
										[hourFormat]="'12'"
										[showSeconds]="false"
										[inline]="false"
										[showOnFocus]="true"
										appendTo="body"
										class="due-date-picker cursor-pointer"
										[pTooltip]="quote.dueDate
												? 'Click to edit'
												: 'Click to set due date'
										"
										[tooltipPosition]="'top'"
										[invalid]="quote.dueDate == null"
										[placeholder]="quote.dueDate == null ? 'Set Due Date' : ''"
										[showButtonBar]="true"
										[hideOnDateTimeSelect]="false"
										panelStyleClass="due-date-panel"
									>
										<ng-template #buttonbar>
											<p-button
												size="small"
												label="Save Due Date"
												(onClick)="saveDueDate(dueDatePicker, quote)"
											/>
										</ng-template>
									</p-date-picker>
								</span>
							</td>
							<td class="col-hours">
								@if (!["Closed Won", "Closed Lost", "Submitted"].includes(quote.nsEstimateStatus ?? "")) {
									@if (quote.hoursRemaining === null) {
										<span class="dash">—</span>
									} @else if (quote.isOverdue) {
										<p-tag
											value="{{ hoursLabel(quote) }}"
											severity="danger"
											styleClass="hours-tag"></p-tag>
									} @else {
										<span
											class="hours-text"
											[class.hours-high]="quote.hoursRemaining >= 50"
											[class.hours-medium]="quote.hoursRemaining < 50">
                                            {{ hoursLabel(quote) }}
                                        </span>
									}
								}
							</td>
							<td
								class="col-customer"
								(click)="$event.stopPropagation()">
								<p-select
									[options]="nsCustomers()"
									[ngModel]="getSelectedNsRecordId(quote)"
									(ngModelChange)="onNsCustomerChange(quote.id, $event)"
									[disabled]="!!quote.nsEstimateId || !!quote.isEstimateCreating"
									optionLabel="name"
									optionValue="id"
									placeholder="— Select customer —"
									[filter]="true"
									filterBy="name"
									class="row-select"
									panelStyleClass="ns-customer-panel"
									appendTo="body"
									[pTooltip]="getNsCustomerTooltip(quote)"
									tooltipPosition="top"></p-select>
							</td>
							<td class="col-summary">
                                <span
									class="summary-text"
									(click)="openEmailModal(quote); $event.stopPropagation()">
                                    {{ quote.emailSummary }}
                                </span>
								@if (quote.hasThreadReplies && quote.threadReplies) {
									<span class="reply-badge">
                                        {{ quote.threadReplies.length }} repl{{
											quote.threadReplies.length === 1 ? "y" : "ies"
										}}
                                    </span>
								}
								@if (quote.attachments?.length) {
									<span class="attachment-badge">
                                        <i class="pi pi-paperclip"></i>
										{{ quote.attachments.length }}
                                    </span>
								}
							</td>
							<td class="col-rep">
								<div class="rep-cell">
                                    <span
										class="avatar"
										[style.background-color]="quote.outsideRep.avatarColor">
                                        {{ quote.outsideRep.initials }}
                                    </span>
									<span class="rep-name">{{ quote.outsideRep.name }}</span>
								</div>
							</td>
							<td
								class="col-isr"
								(click)="$event.stopPropagation()">
								@if (quote.pendingIsrRep; as pendingRep) {
									<div class="isr-pending">
										<span class="isr-pending-text">Assigning to {{ pendingRep.fullName }}…</span>
										<button
											type="button"
											class="isr-undo-btn"
											(click)="undoInsideRepAssignment(quote.id)">
											Undo
										</button>
									</div>
								} @else {
									<p-select
										[options]="insideSalesReps()"
										[ngModel]="getSelectedIsr(quote)"
										(ngModelChange)="setInsideRep(quote.id, $event)"
										[disabled]="
                                            quote.isEstimateCreating ||
                                            !quote.dueDate ||
                                            !quote.nsCustomerId ||
                                            !userIsSalesManager
                                        "
										optionLabel="fullName"
										placeholder="— Assign —"
										[filter]="true"
										filterBy="fullName"
										[showClear]="!quote.nsEstimateId"
										styleClass="row-select"
										appendTo="body"
										[pTooltip]="getIsrTooltip(quote)"
										tooltipPosition="top"></p-select>
								}
							</td>
							<td
								class="col-estimate"
								(click)="$event.stopPropagation()">
								@if (quote.isEstimateCreating) {
									<span class="estimate-loading">
                                        <i class="pi pi-spin pi-spinner"></i>
                                    </span>
								} @else if (quote.estimateNumber && quote.nsEstimateId) {
									<a
										class="estimate-link"
										[href]="
                                            'https://1103055.app.netsuite.com/app/accounting/transactions/estimate.nl?id=' +
                                            quote.nsEstimateId
                                        "
										target="_blank">
										{{ quote.estimateNumber }}
										<i
											class="pi pi-external-link"
											style="font-size: 11px"></i>
									</a>
								} @else {
									<span class="dash">—</span>
								}
							</td>
							<td class="col-ns-status">
								<span>{{ quote.nsEstimateStatus ?? "—" }}</span>
							</td>
							<td class="col-quote-status">
								<span>{{ quote.nsQuoteStatus ?? "—" }}</span>
							</td>
							<td class="col-errors">
								@if (customerIsDefaultOrEmpty(quote)) {
									<span
										pTooltip="No valid NetSuite customer match was detected — this quote will default to a Credit Card Pre-Sale. Pick the correct customer if that is wrong."
										tooltipPosition="left">
                                        <i
											class="pi pi-exclamation-triangle embark-status-icon embark-status-icon--warning"></i>
                                    </span>
								} @else {
									<span
										pTooltip="NetSuite customer matched"
										tooltipPosition="left">
                                        <i class="pi pi-info-circle embark-status-icon embark-status-icon--ok"></i>
                                    </span>
								}
							</td>
							<td
								class="col-quote-actions"
								(click)="$event.stopPropagation()">
								<p-button
									icon="pi pi-trash"
									severity="danger"
									[text]="true"
									[rounded]="true"
									pTooltip="Delete quote"
									tooltipPosition="left"
									(onClick)="confirmDeleteQuote(quote)"></p-button>
							</td>
						</tr>
					</ng-template>

					<ng-template pTemplate="emptymessage">
						<tr>
							<td
								colspan="12"
								class="empty-message">
								@if (isLoading()) {
									<div class="empty-loading">
										<app-embark-spinner
											[size]="'3'"
											[strokeWidth]="'6'"></app-embark-spinner>
										<p>Loading quotes...</p>
									</div>
								} @else {
									<i
										class="pi pi-inbox"
										style="font-size: 2rem; color: #d1d5db"></i>
									<p>No quotes found</p>
								}
							</td>
						</tr>
					</ng-template>
				</p-table>
			</div>
		}
	</div>
</div>

<!-- Email Detail Dialog -->
<p-dialog
	[(visible)]="showEmailModalValue"
	[modal]="true"
	[draggable]="false"
	[resizable]="false"
	header="Email Details"
	styleClass="email-dialog"
	[style]="{ width: '700px', maxHeight: '85vh' }"
	[maximizable]="false"
	appendTo="body">
	@if (selectedQuote(); as quote) {
		<div class="dialog-body">
			<div class="dialog-section">
				<div class="dialog-section-title">Quote Details</div>
				<div class="dialog-grid">
					<div class="dialog-field">
						<label>Customer</label>
						<span>{{ quote.customer }}</span>
					</div>
					<div class="dialog-field">
						<label>Status</label>
						<p-tag [value]="quote.status === 'assigned' ? 'Assigned' : 'Unassigned'"></p-tag>
					</div>
					<div class="dialog-field">
						<label>Received</label>
						<span>{{ quote.receivedLabel }}</span>
					</div>
					<div class="dialog-field">
						<label>Due</label>
						<span>
                            {{ quote.dueLabel }}
							@if (quote.dueDateDefaulted) {
								<span
									class="defaulted-badge-inline"
									pTooltip="Defaulted to 7 days — not found in email">
                                    defaulted
                                </span>
							}
                        </span>
					</div>
					<div class="dialog-field">
						<label>Outside Rep</label>
						<span>{{ quote.outsideRep.name }}</span>
					</div>
					@if (quote.emailWebLink) {
						<div class="dialog-field">
							<label>Original Email</label>
							<a
								class="email-link"
								[href]="quote.emailWebLink"
								target="_blank">
								<i class="pi pi-envelope"></i>
								Open in Outlook
								<i
									class="pi pi-external-link"
									style="font-size: 10px"></i>
							</a>
						</div>
					}
				</div>
			</div>

			<div class="dialog-section">
				<div class="dialog-section-title">Email Summary</div>
				<div class="dialog-summary-body">{{ quote.emailSummary }}</div>
			</div>

			@if (quote.attachments.length) {
				<div class="dialog-section">
					<div class="dialog-section-title">
						<i
							class="pi pi-paperclip"
							style="margin-right: 6px"></i>
						Attachments ({{ quote.attachments.length }})
					</div>
					<div class="dialog-attachments">
						@for (att of quote.attachments; track att.id) {
							<div
								class="attachment-item"
								(click)="downloadAttachment(att)">
								<i
									class="pi {{
                                        att.contentType === 'application/pdf' ? 'pi-file-pdf' : 'pi-file-excel'
                                    }}"></i>
								<span class="attachment-name">{{ att.name }}</span>
								<span class="attachment-size">{{ (att.size / 1024).toFixed(0) }} KB</span>
								<i class="pi pi-download"></i>
							</div>
						}
					</div>
				</div>
			}

			@if (quote.attachmentsOmitted) {
				<div class="attachments-omitted-warning">
					<i class="pi pi-exclamation-triangle"></i>
					Attachment size exceeded — see original email thread for the files
				</div>
			}

			@if (quote.hasThreadReplies && quote.threadReplyCount && quote.threadReplyCount > 0) {
				<div class="dialog-section">
					<div class="dialog-section-title">
						<i
							class="pi pi-comments"
							style="margin-right: 6px"></i>
						Thread Replies ({{ quote.threadReplyCount }})
					</div>
					<div class="dialog-replies">
						@for (reply of quote.threadReplies; track reply.receivedAt) {
							<div class="thread-reply">
								<div class="thread-reply-header">
									<div class="thread-reply-sender">
										<span class="thread-avatar">{{ reply.from.charAt(0).toUpperCase() }}</span>
										<div class="thread-reply-meta">
											<span class="thread-reply-from">{{ reply.from }}</span>
											<span class="thread-reply-subject">{{ reply.subject }}</span>
										</div>
									</div>
									<span class="thread-reply-date">
                                        {{ reply.receivedAt | date: "M/d/yyyy h:mm a" }}
                                    </span>
								</div>
								<div class="thread-reply-body">{{ reply.body }}</div>
							</div>
						}
					</div>
				</div>
			}
			<div style="height: 16px"></div>
		</div>
	}
</p-dialog>

<!-- Detail Popover Dialog -->
<p-dialog
	[(visible)]="showDetailPopoverValue"
	[modal]="true"
	[draggable]="false"
	[resizable]="false"
	header="Quote Details"
	styleClass="detail-dialog"
	[style]="{ width: '88vw', maxHeight: '85vh' }"
	appendTo="body">
	@if (selectedDetailQuote(); as quote) {
		<div class="detail-dialog-body">
			<!-- Left Panel -->
			<div class="detail-left">
				<div class="detail-header">
					<span class="detail-customer">{{ quote.customer }}</span>
					<span class="detail-received">Received {{ quote.receivedLabel }} · Due {{ quote.dueLabel }}</span>
				</div>

				<div class="detail-section">
					<div class="detail-field">
						<label>Outside Rep</label>
						<div class="rep-cell">
                            <span
								class="avatar"
								[style.background-color]="quote.outsideRep.avatarColor">
                                {{ quote.outsideRep.initials }}
                            </span>
							<span>{{ quote.outsideRep.name }}</span>
						</div>
					</div>
					<div class="detail-field">
						<label>Inside Rep</label>
						<span>{{ quote.insideRep ?? "—" }}</span>
					</div>
					<div class="detail-field">
						<label>NS Status</label>
						<span>{{ quote.nsEstimateStatus ?? "—" }}</span>
					</div>
					<div class="detail-field">
						<label>Quote Status</label>
						<span>{{ quote.nsQuoteStatus ?? "—" }}</span>
					</div>
					@if (quote.emailWebLink) {
						<div class="detail-field">
							<label>Source Email</label>
							<a
								class="email-link"
								[href]="quote.emailWebLink"
								target="_blank">
								<i class="pi pi-envelope"></i>
								Open in Outlook
							</a>
						</div>
					}
				</div>

				<div class="detail-section">
					<div class="detail-section-title">Email Summary</div>
					<div class="detail-summary">{{ quote.emailSummary }}</div>
				</div>

				@if (quote.attachments.length) {
					<div class="detail-section">
						<div class="detail-section-title">
							<i
								class="pi pi-paperclip"
								style="margin-right: 6px"></i>
							Attachments ({{ quote.attachments.length }})
						</div>
						<div class="dialog-attachments">
							@for (att of quote.attachments; track att.id) {
								<div
									class="attachment-item"
									(click)="downloadAttachment(att)">
									<i
										class="pi {{
                                            att.contentType === 'application/pdf' ? 'pi-file-pdf' : 'pi-file-excel'
                                        }}"></i>
									<span class="attachment-name">{{ att.name }}</span>
									<span class="attachment-size">{{ (att.size / 1024).toFixed(0) }} KB</span>
									<i class="pi pi-download"></i>
								</div>
							}
						</div>
					</div>
				}

				@if (quote.attachmentsOmitted) {
					<div class="attachments-omitted-warning">
						<i class="pi pi-exclamation-triangle"></i>
						Attachment size exceeded — see original email thread for the files
					</div>
				}
			</div>

			<!-- Right Panel: item matching -->
			<div class="detail-right">
				<div class="detail-section-title">
					Item matching
					<span class="detail-subtitle">
                        Review the matched parts, correct anything wrong, then export
						@if (matchSourceList() === "marketbasket") {
							· matching against this customer's market basket
						}
                    </span>
				</div>
				<div>
					@if (customerNotes()) {
						<div class="match-banner match-banner-info">
							<i class="pi pi-info-circle"></i>
							<span>
                                <strong>Customer notes</strong>
                                <small>{{ customerNotes() }}</small>
                            </span>
						</div>
					}
				</div>

				<div class="detail-content-scroll">
					@if (!canMatchItems(quote)) {
						<div class="detail-empty">
							<i class="pi pi-info-circle"></i>
							Assign a NetSuite customer to this quote before matching items — the customer determines
							which item list applies.
						</div>
					} @else if (itemMatchesLoading()) {
						<div class="detail-loading">
							<i class="pi pi-spin pi-spinner"></i>
							@if (isExtracting()) {
								<span>
                                    Reading line items from the email and attachments…
                                    <small>
                                        This can take a while depending on the size and number of attachments.
                                    </small>
                                </span>
							} @else {
								<span>Loading item matches…</span>
							}
						</div>
					} @else {
						@if (basketUnavailable()) {
							<div class="match-banner match-banner-error">
								<i class="pi pi-exclamation-triangle"></i>
								<span>
                                    This customer's market basket could not be loaded, so no items can be matched. This
                                    is not a problem with the email.
									@if (basketError()) {
										<small>{{ basketError() }}</small>
									}
                                </span>
							</div>
						}

						<!-- @if (customerNotes()) {
							<div class="match-banner match-banner-info">
								<i class="pi pi-info-circle"></i>
								<span>
									<strong>Customer notes</strong>
									<small>{{ customerNotes() }}</small>
								</span>
							</div>
						} -->

						@if (problemAttachments().length) {
							<div class="match-banner match-banner-warn">
								<i class="pi pi-paperclip"></i>
								<div class="attachment-status-list">
									@for (att of problemAttachments(); track att.fileName) {
										<div class="attachment-status">
											<p-tag
												[value]="att.status"
												[severity]="attachmentSeverity(att.status)"></p-tag>
											<span class="attachment-status-name">
                                                {{ att.fileName ?? "(unnamed)" }}
                                            </span>
											@if (att.detail) {
												<span class="attachment-status-detail">{{ att.detail }}</span>
											}
										</div>
									}
								</div>
							</div>
						}

						@if (itemMatches().length === 0) {
							<div class="detail-empty">No line items were found in this email or its attachments.</div>
						} @else {
							<table class="match-table">
								<thead>
								<tr>
									<th class="col-mark">Mark Number</th>
									<th class="col-qty">Quantity</th>
									<th class="col-cust-desc">Customer Description</th>
									<th class="col-part">RK Part Code</th>
									<th class="col-ns-desc">NS RK Description</th>
									<th class="col-confidence">Confidence</th>
									<th class="col-actions"></th>
								</tr>
								</thead>
								<tbody>
									@for (row of itemMatches(); track row.displayOrder) {
										<tr [class.match-row-unmatched]="!row.matchFound">
											<td class="col-mark">
												<input
													pInputText
													type="text"
													class="match-input"
													[ngModel]="row.markNumber"
													(ngModelChange)="onMarkNumberChange(row, $event)"/>
											</td>
											<td class="col-qty">
												<input
													pInputText
													type="text"
													class="match-input"
													[ngModel]="row.quantity"
													(ngModelChange)="onQuantityChange(row, $event)"/>
											</td>
											<td class="col-cust-desc">
												{{ row.customerDescription ?? "—" }}
												@if (row.isSuspectedDuplicate) {
													<p-tag
														value="Possible duplicate"
														severity="info"
														[pTooltip]="'This description also appears on another row — often a BOM restated in both the body and an attachment.'"
														tooltipPosition="top"></p-tag>
												}
											</td>
											<td class="col-part">
												<p-select
													[options]="optionsForRow(row)"
													[ngModel]="row.nsInternalId"
													(ngModelChange)="onPartCodeChange(row, $event)"
													optionLabel="itemId"
													optionValue="nsInternalId"
													placeholder="— Select part —"
													[filter]="true"
													filterBy="itemId,description"
													(onFilter)="onItemFilter(row, $event.filter)">
													<ng-template
														pTemplate="item"
														let-item>
														<div class="item-option">
                                                            <span class="item-option-desc">
                                                                {{ item.description ?? "—" }}
                                                            </span>
															<span class="item-option-code">{{ item.itemId }}</span>
														</div>
													</ng-template>
												</p-select>
											</td>
											<td class="col-ns-desc">{{ row.nsDescription ?? "—" }}</td>
											<td class="col-confidence">
												@if (row.isUserOverridden || row.isManuallyAdded) {
													<p-tag
														value="Manual"
														severity="secondary"></p-tag>
												} @else if (!row.matchFound) {
													<p-tag
														value="No match"
														severity="danger"></p-tag>
												} @else if (isLowConfidence(row)) {
													<p-tag
														[value]="confidenceLabel(row)"
														severity="warn"></p-tag>
												} @else {
													<span>{{ confidenceLabel(row) }}</span>
												}
											</td>
											<td class="col-actions">
												<p-button
													icon="pi pi-trash"
													severity="danger"
													[text]="true"
													[rounded]="true"
													pTooltip="Remove this item"
													tooltipPosition="left"
													(onClick)="deleteMatchRow(row)"></p-button>
											</td>
										</tr>
									}
								</tbody>
							</table>
						}
					}
				</div>

				<div class="match-actions">
					<div class="match-actions-row">
						<p-button
							label="Add Item"
							icon="pi pi-plus"
							severity="secondary"
							[outlined]="true"
							(onClick)="addMatchRow()"></p-button>
						<p-button
							label="Re-read Email"
							icon="pi pi-refresh"
							severity="secondary"
							[text]="true"
							pTooltip="Run extraction again. Manual corrections are kept."
							tooltipPosition="top"
							(onClick)="refreshItemMatches()"></p-button>
					</div>
					<p-button
						label="Export and Save"
						icon="pi pi-file-excel"
						[loading]="isExporting()"
						[disabled]="itemMatches().length === 0"
						(onClick)="exportItemMatches()"
						class="match-actions-export"></p-button>
				</div>
			</div>
		</div>
	}
</p-dialog>

<!-- Export Dialog -->
<p-dialog
	[(visible)]="showExportDialogValue"
	[modal]="true"
	[draggable]="false"
	[resizable]="false"
	[blockScroll]="true"
	header="Download Quote Log"
	styleClass="export-dialog"
	[style]="{ width: '520px' }"
	appendTo="body">
	<div class="export-dialog-body">
		<p>Select the received date range to export.</p>
		<div class="export-dialog-fields">
			<div class="export-dialog-field">
				<label>Start Date</label>
				<p-datepicker
					[(ngModel)]="exportStartDate"
					placeholder="Start Date"
					size="small"
					[fluid]="true"
					[showIcon]="true"
					[showClear]="true"
					[showOnFocus]="false"
					appendTo="body"></p-datepicker>
			</div>
			<div class="export-dialog-field">
				<label>End Date</label>
				<p-datepicker
					[(ngModel)]="exportEndDate"
					placeholder="End Date"
					size="small"
					[fluid]="true"
					[showIcon]="true"
					[showClear]="true"
					[showOnFocus]="false"
					appendTo="body"></p-datepicker>
			</div>
		</div>
	</div>
	<div class="dialog-footer">
		<p-button
			label="Cancel"
			[text]="true"
			severity="secondary"
			(onClick)="showExportDialogValue = false"></p-button>
		<p-button
			label="Export"
			icon="pi pi-download"
			(onClick)="confirmExport()"></p-button>
	</div>
</p-dialog>
